/**
 * Task Recovery Hook
 *
 * Recovers active tasks from localStorage on page load and resumes tracking.
 * Syncs task status with backend via GraphQL and handles completion/failure.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, gql } from '@apollo/client';
import { useKeycloakAuth } from '../contexts/KeycloakAuthProvider';
import {
  StoredTask,
  getStoredTasks,
  getActiveTasks,
  updateTaskStatus,
  removeTask,
  cleanupExpiredTasks,
  saveActiveTask,
  clearAllTasks as clearAllTasksFromStorage
} from '../services/taskStorage';
import { TaskStatus, TaskState, isTaskTerminal } from '../types/task';

// ============================================================================
// GraphQL Queries (inline - will work once schema is regenerated)
// ============================================================================

const FIND_TASK_BY_TASK_ID = gql`
  query FindTaskByTaskId($taskId: String!) {
    findTaskByTaskId(taskId: $taskId) {
      id
      taskId
      taskType
      status
      progressPercentage
      progressMessage
      startedAt
      completedAt
      errorData
    }
  }
`;

const _FIND_TASKS_BY_USER = gql`
  query FindTasksByUser($userId: String!, $status: String) {
    findTasksByUser(userId: $userId, status: $status) {
      id
      taskId
      taskType
      status
      progressPercentage
      progressMessage
      createdAt
      startedAt
      completedAt
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

interface TaskRecoveryOptions {
  /** Polling interval in milliseconds for active tasks (default: 5000) */
  pollingInterval?: number;
  /** Maximum consecutive errors before stopping polling (default: 5) */
  maxConsecutiveErrors?: number;
  /** Callback when a task completes */
  onTaskComplete?: (task: StoredTask, result?: TaskState) => void;
  /** Callback when a task fails */
  onTaskFailed?: (task: StoredTask, error?: unknown) => void;
  /** Enable auto-recovery on mount (default: true) */
  autoRecover?: boolean;
}

interface UseTaskRecoveryResult {
  /** Currently tracked tasks from localStorage */
  trackedTasks: StoredTask[];
  /** Tasks that are actively being polled */
  activeTasks: StoredTask[];
  /** Recovery in progress */
  isRecovering: boolean;
  /** Last recovery error */
  error: string | null;
  /** Manually trigger task recovery */
  recoverTasks: () => Promise<void>;
  /** Start tracking a new task */
  trackTask: (taskId: string, taskType: StoredTask['taskType'], namespace?: string, metadata?: StoredTask['metadata']) => void;
  /** Stop tracking a task */
  stopTracking: (taskId: string) => void;
  /** Get fresh status for a task from backend */
  refreshTaskStatus: (taskId: string) => Promise<TaskState | null>;
  /** Clear all tracked tasks */
  clearAllTasks: () => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export const useTaskRecovery = (options: TaskRecoveryOptions = {}): UseTaskRecoveryResult => {
  const { pollingInterval = 5000, maxConsecutiveErrors = 5, onTaskComplete, onTaskFailed, autoRecover = true } = options;

  const apolloClient = useApolloClient();
  const { user } = useKeycloakAuth();

  // State
  const [trackedTasks, setTrackedTasks] = useState<StoredTask[]>([]);
  const [isRecovering, setIsRecovering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for polling control
  const mountedRef = useRef(true);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const consecutiveErrorsRef = useRef(0);
  const isPollingRef = useRef(false);

  // Get active tasks (non-terminal)
  const activeTasks = trackedTasks.filter((t) => !isTaskTerminal(t.status));

  /**
   * Fetch task status from backend via GraphQL
   */
  const fetchTaskStatus = useCallback(
    async (taskId: string): Promise<TaskState | null> => {
      try {
        const result = await apolloClient.query({
          query: FIND_TASK_BY_TASK_ID,
          variables: { taskId },
          fetchPolicy: 'network-only'
        });

        return result.data?.findTaskByTaskId || null;
      } catch (err) {
        console.error(`[TaskRecovery] Failed to fetch task ${taskId}:`, err);
        return null;
      }
    },
    [apolloClient]
  );

  /**
   * Sync a stored task with backend status
   */
  const syncTaskWithBackend = useCallback(
    async (storedTask: StoredTask): Promise<{ updated: boolean; task: StoredTask }> => {
      const backendTask = await fetchTaskStatus(storedTask.taskId);

      if (!backendTask) {
        // Task not found in backend - mark as unknown and keep for now
        return { updated: false, task: storedTask };
      }

      const backendStatus = backendTask.status as TaskStatus;
      const hasChanged =
        backendStatus !== storedTask.status ||
        backendTask.progressPercentage !== storedTask.progressPercentage ||
        backendTask.progressMessage !== storedTask.progressMessage;

      if (hasChanged) {
        // Update localStorage
        updateTaskStatus(storedTask.taskId, backendStatus, backendTask.progressPercentage, backendTask.progressMessage || undefined);

        const updatedTask: StoredTask = {
          ...storedTask,
          status: backendStatus,
          progressPercentage: backendTask.progressPercentage,
          progressMessage: backendTask.progressMessage || undefined
        };

        // Handle completion/failure callbacks
        if (isTaskTerminal(backendStatus)) {
          if (backendStatus === 'COMPLETED') {
            onTaskComplete?.(updatedTask, backendTask);
          } else if (backendStatus === 'FAILED' || backendStatus === 'CANCELLED') {
            onTaskFailed?.(updatedTask, backendTask.errorData);
          }
        }

        return { updated: true, task: updatedTask };
      }

      return { updated: false, task: storedTask };
    },
    [fetchTaskStatus, onTaskComplete, onTaskFailed]
  );

  /**
   * Recover tasks from localStorage and sync with backend
   */
  const recoverTasks = useCallback(async () => {
    if (!user?.id) return;

    setIsRecovering(true);
    setError(null);

    try {
      // Clean up expired tasks first
      cleanupExpiredTasks();

      // Get all stored tasks
      const storedTasks = getStoredTasks();

      if (storedTasks.length === 0) {
        setTrackedTasks([]);
        return;
      }

      // Sync each task with backend
      const syncedTasks: StoredTask[] = [];

      for (const task of storedTasks) {
        if (!mountedRef.current) break;

        const { task: syncedTask } = await syncTaskWithBackend(task);
        syncedTasks.push(syncedTask);
      }

      setTrackedTasks(syncedTasks);
      consecutiveErrorsRef.current = 0;
    } catch (err) {
      console.error('[TaskRecovery] Recovery failed:', err);
      setError(err instanceof Error ? err.message : 'Recovery failed');
      // Still load stored tasks even if sync fails
      setTrackedTasks(getStoredTasks());
    } finally {
      setIsRecovering(false);
    }
  }, [user?.id, syncTaskWithBackend]);

  /**
   * Poll active tasks for updates
   */
  const pollActiveTasks = useCallback(async () => {
    if (isPollingRef.current || !mountedRef.current) return;
    if (consecutiveErrorsRef.current >= maxConsecutiveErrors) {
      console.warn('[TaskRecovery] Max consecutive errors reached, stopping polling');
      return;
    }

    const currentActiveTasks = getActiveTasks();
    if (currentActiveTasks.length === 0) return;

    isPollingRef.current = true;

    try {
      const updatedTasks: StoredTask[] = [];
      let hasError = false;

      for (const task of currentActiveTasks) {
        if (!mountedRef.current) break;

        try {
          const { task: syncedTask } = await syncTaskWithBackend(task);
          updatedTasks.push(syncedTask);
        } catch {
          hasError = true;
          updatedTasks.push(task);
        }
      }

      if (hasError) {
        consecutiveErrorsRef.current += 1;
      } else {
        consecutiveErrorsRef.current = 0;
      }

      // Update state with synced tasks
      setTrackedTasks((prev) => {
        const terminalTasks = prev.filter((t) => isTaskTerminal(t.status));
        return [...updatedTasks, ...terminalTasks.filter((t) => !updatedTasks.some((u) => u.taskId === t.taskId))];
      });
    } finally {
      isPollingRef.current = false;
    }
  }, [maxConsecutiveErrors, syncTaskWithBackend]);

  /**
   * Start tracking a new task
   */
  const trackTask = useCallback((taskId: string, taskType: StoredTask['taskType'], namespace?: string, metadata?: StoredTask['metadata']) => {
    const newTask: Omit<StoredTask, 'createdAt' | 'lastChecked'> = {
      taskId,
      taskType,
      namespace,
      status: 'PENDING',
      progressPercentage: 0,
      metadata
    };

    saveActiveTask(newTask);

    setTrackedTasks((prev) => {
      const existing = prev.findIndex((t) => t.taskId === taskId);
      const storedTask: StoredTask = {
        ...newTask,
        createdAt: new Date().toISOString(),
        lastChecked: new Date().toISOString()
      };

      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = storedTask;
        return updated;
      }
      return [...prev, storedTask];
    });
  }, []);

  /**
   * Stop tracking a task
   */
  const stopTracking = useCallback((taskId: string) => {
    removeTask(taskId);
    setTrackedTasks((prev) => prev.filter((t) => t.taskId !== taskId));
  }, []);

  /**
   * Refresh task status from backend
   */
  const refreshTaskStatus = useCallback(
    async (taskId: string): Promise<TaskState | null> => {
      const backendTask = await fetchTaskStatus(taskId);

      if (backendTask) {
        const backendStatus = backendTask.status as TaskStatus;
        updateTaskStatus(taskId, backendStatus, backendTask.progressPercentage, backendTask.progressMessage || undefined);

        setTrackedTasks((prev) =>
          prev.map((t) =>
            t.taskId === taskId
              ? {
                  ...t,
                  status: backendStatus,
                  progressPercentage: backendTask.progressPercentage,
                  progressMessage: backendTask.progressMessage || undefined
                }
              : t
          )
        );
      }

      return backendTask;
    },
    [fetchTaskStatus]
  );

  /**
   * Clear all tracked tasks
   */
  const clearAllTasks = useCallback(() => {
    clearAllTasksFromStorage();
    setTrackedTasks([]);
  }, []);

  // Auto-recover on mount
  useEffect(() => {
    if (autoRecover && user?.id) {
      recoverTasks();
    }
  }, [autoRecover, user?.id, recoverTasks]);

  // Polling effect for active tasks
  useEffect(() => {
    if (activeTasks.length === 0) {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
      return;
    }

    // Start polling
    pollingTimerRef.current = setInterval(() => {
      pollActiveTasks();
    }, pollingInterval);

    return () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [activeTasks.length, pollingInterval, pollActiveTasks]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, []);

  return {
    trackedTasks,
    activeTasks,
    isRecovering,
    error,
    recoverTasks,
    trackTask,
    stopTracking,
    refreshTaskStatus,
    clearAllTasks
  };
};

export default useTaskRecovery;
